import { useEffect, useState } from 'react';

import { ArrowLeft, Copy, Loader2, Save, Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useCreateScenario,
  useDeleteScenario,
  useDuplicateScenario,
  useScenario,
  useUpdateScenario,
} from '@/hooks/useScenarios';

import { AssignmentsCard } from './components/AssignmentsCard';
import { ExpectationsEditor } from './components/ExpectationsEditor';
import { RunHistoryCard } from './components/RunHistoryCard';
import {
  emptyExpectations,
  normalizeExpectations,
  serializeExpectations,
  validateExpectations,
} from './expectations';

import type { NewScenarioRouterState } from './components/ScenarioDraftDialog';
import type {
  CreateScenarioInput,
  Scenario,
  ScenarioDraft,
  ScenarioExpectations,
} from '@/lib/api/scenarios.api';

const DEFAULT_TURN_CAP = 30;
const LIST_PATH = '/agent-improvement/scenarios';

interface FormState {
  name: string;
  description: string;
  tags: string;
  persona: string;
  goal: string;
  facts: string;
  objections: string;
  openingLine: string;
  turnCap: number;
  expectations: ScenarioExpectations;
}

const linesToList = (text: string) =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const commaToList = (text: string) =>
  Array.from(
    new Set(
      text
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    ),
  );

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  tags: '',
  persona: '',
  goal: '',
  facts: '',
  objections: '',
  openingLine: '',
  turnCap: DEFAULT_TURN_CAP,
  expectations: emptyExpectations(),
});

const formFromDraft = (draft: ScenarioDraft, description?: string): FormState => ({
  name: draft.name ?? '',
  description: draft.description ?? description ?? '',
  tags: (draft.tags ?? []).join(', '),
  persona: draft.brief?.persona ?? '',
  goal: draft.brief?.goal ?? '',
  facts: (draft.brief?.facts ?? []).join('\n'),
  objections: (draft.brief?.objections ?? []).join('\n'),
  openingLine: draft.brief?.openingLine ?? '',
  turnCap: draft.turnCap ?? DEFAULT_TURN_CAP,
  expectations: normalizeExpectations(draft.expectations),
});

const formFromScenario = (s: Scenario): FormState =>
  formFromDraft({
    name: s.name,
    description: s.description,
    tags: s.tags,
    brief: s.brief,
    expectations: s.expectations,
    turnCap: s.turn_cap,
  });

const formToInput = (form: FormState): CreateScenarioInput => ({
  name: form.name.trim(),
  description: form.description.trim(),
  tags: commaToList(form.tags),
  brief: {
    persona: form.persona.trim(),
    goal: form.goal.trim(),
    facts: linesToList(form.facts),
    objections: linesToList(form.objections),
    openingLine: form.openingLine.trim(),
  },
  expectations: serializeExpectations(form.expectations),
  turnCap: form.turnCap,
});

const validateForm = (form: FormState): string | null => {
  if (!form.name.trim()) return 'Name is required';
  if (!form.persona.trim()) return 'Persona is required';
  if (!form.goal.trim()) return 'Goal is required';
  if (!form.openingLine.trim()) return 'Opening line is required';
  if (!Number.isInteger(form.turnCap) || form.turnCap < 2 || form.turnCap > 60) {
    return 'Turn cap must be a whole number between 2 and 60';
  }
  return validateExpectations(serializeExpectations(form.expectations));
};

const textareaClass =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none';

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}

const Field = ({ id, label, hint, children }: FieldProps) => (
  <div className="space-y-1">
    <label htmlFor={id} className="text-xs font-medium">
      {label}
    </label>
    {children}
    {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
  </div>
);

export const ScenarioEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const scenarioId = isNew ? undefined : id;
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const { data: scenario, isLoading } = useScenario(scenarioId);
  const createMutation = useCreateScenario();
  const updateMutation = useUpdateScenario();
  const deleteMutation = useDeleteScenario();
  const duplicateMutation = useDuplicateScenario();

  const [form, setForm] = useState<FormState>(() => {
    if (!isNew) return emptyForm();
    const state = (location.state ?? {}) as NewScenarioRouterState;
    return state.draft
      ? formFromDraft(state.draft, state.description)
      : {
          ...emptyForm(),
          description: state.description ?? '',
        };
  });
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  usePageTitle(isNew ? 'New scenario' : (scenario?.name ?? 'Scenario'));

  // Populate the form once per scenario id; later refetches must not clobber edits.
  useEffect(() => {
    if (scenario && scenario.id !== loadedId) {
      setForm(formFromScenario(scenario));
      setLoadedId(scenario.id);
    }
  }, [scenario, loadedId]);

  const patch = (changes: Partial<FormState>) => setForm((prev) => ({ ...prev, ...changes }));

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    const error = validateForm(form);
    if (error) {
      toast({ variant: 'destructive', title: 'Check the form', description: error });
      return;
    }
    const input = formToInput(form);
    try {
      if (scenarioId) {
        await updateMutation.mutateAsync({ id: scenarioId, input });
      } else {
        const created = await createMutation.mutateAsync(input);
        navigate(`${LIST_PATH}/${created.id}`, { replace: true });
      }
    } catch {
      // Error toast is raised by the hook.
    }
  };

  const handleDuplicate = async () => {
    if (!scenarioId) return;
    try {
      const copy = await duplicateMutation.mutateAsync(scenarioId);
      navigate(`${LIST_PATH}/${copy.id}`);
    } catch {
      // Error toast is raised by the hook.
    }
  };

  const handleDelete = async () => {
    if (!scenarioId) return;
    try {
      await deleteMutation.mutateAsync(scenarioId);
      navigate(LIST_PATH, { replace: true });
    } catch {
      // Error toast is raised by the hook.
    } finally {
      setDeleteOpen(false);
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!isNew && !scenario) {
    return (
      <div className="space-y-4 p-6">
        <p className="text-muted-foreground text-sm">Scenario not found.</p>
        <Button variant="outline" onClick={() => navigate(LIST_PATH)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to scenarios
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(LIST_PATH)}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Scenarios
          </Button>
          <h1 className="text-lg font-semibold">{isNew ? 'New scenario' : scenario?.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {scenarioId && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDuplicate}
                disabled={duplicateMutation.isPending}
              >
                {duplicateMutation.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Copy className="mr-1 h-4 w-4" />
                )}
                Duplicate
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
            </>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            {isNew ? 'Create scenario' : 'Save'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Scenario</CardTitle>
          <CardDescription>What this test is about and how to find it later.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field id="scenario-name" label="Name">
              <Input
                id="scenario-name"
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="Used Golf test drive booking"
              />
            </Field>
            <Field id="scenario-tags" label="Tags" hint="Comma-separated.">
              <Input
                id="scenario-tags"
                value={form.tags}
                onChange={(e) => patch({ tags: e.target.value })}
                placeholder="booking, used-cars"
              />
            </Field>
          </div>
          <Field
            id="scenario-description"
            label="Description"
            hint="The one-paragraph source of this scenario."
          >
            <textarea
              id="scenario-description"
              rows={3}
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              className={textareaClass}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customer brief</CardTitle>
          <CardDescription>
            What the simulated customer knows and wants. The simulator follows this verbatim.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field id="brief-persona" label="Persona">
              <Input
                id="brief-persona"
                value={form.persona}
                onChange={(e) => patch({ persona: e.target.value })}
                placeholder="Returning customer in her 40s, polite but in a hurry"
              />
            </Field>
            <Field id="brief-goal" label="Goal">
              <Input
                id="brief-goal"
                value={form.goal}
                onChange={(e) => patch({ goal: e.target.value })}
                placeholder="Book a test drive for the Golf this Saturday"
              />
            </Field>
          </div>
          <Field id="brief-opening" label="Opening line">
            <Input
              id="brief-opening"
              value={form.openingLine}
              onChange={(e) => patch({ openingLine: e.target.value })}
              placeholder="Hi, I saw a white Golf on your website, is it still available?"
            />
          </Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field id="brief-facts" label="Facts" hint="One per line.">
              <textarea
                id="brief-facts"
                rows={5}
                value={form.facts}
                onChange={(e) => patch({ facts: e.target.value })}
                className={textareaClass}
                placeholder={'Lives 20 minutes from the showroom\nFree on Saturday morning'}
              />
            </Field>
            <Field id="brief-objections" label="Objections" hint="One per line.">
              <textarea
                id="brief-objections"
                rows={5}
                value={form.objections}
                onChange={(e) => patch({ objections: e.target.value })}
                className={textareaClass}
                placeholder={'Does not want to hear finance offers\nAsks why the price went up'}
              />
            </Field>
          </div>
          <Field
            id="brief-turn-cap"
            label="Turn cap"
            hint="Maximum number of turns the simulated conversation may take (2-60)."
          >
            <Input
              id="brief-turn-cap"
              type="number"
              min={2}
              max={60}
              className="w-32"
              value={form.turnCap}
              onChange={(e) => patch({ turnCap: Number(e.target.value) })}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Expectations</CardTitle>
          <CardDescription>What must happen for the scenario to pass.</CardDescription>
        </CardHeader>
        <CardContent>
          <ExpectationsEditor
            value={form.expectations}
            onChange={(expectations) => patch({ expectations })}
          />
        </CardContent>
      </Card>

      {scenarioId && (
        <>
          <AssignmentsCard scenarioId={scenarioId} />
          <RunHistoryCard scenarioId={scenarioId} />
        </>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete scenario</AlertDialogTitle>
            <AlertDialogDescription>
              This removes &quot;{scenario?.name}&quot; and all of its assignments. Past run results
              are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
